import React, { useState } from 'react'

import { observer } from 'mobx-react'

import { useStores } from 'stores'

import Button from 'common/Button'
import LayoutTitle from 'common/LayoutTitle'
import Loader from 'common/Loader'

import PendingActionGroup from './PendingActionGroup'

import { Tooltip } from 'reactstrap'

export default observer(() => {
  const { authStore, pageResourceStore, pendingActionStore } = useStores()
  const downloadLinkRef = React.useRef()

  const {
    AppSession: { session },
  } = authStore
  const {
    PageResource: { loading: pageResourceLoading, pendingaction: resource = {}, common: commonResource },
  } = pageResourceStore
  const {
    Table: { loading, data },
  } = pendingActionStore
  const { overdueInvoices = [], proposalNotSelected = [], noPoReceived = [], praForApproval = [] } = data
  const pageLoading = loading || pageResourceLoading
  const hasData = overdueInvoices.length || proposalNotSelected.length || noPoReceived.length || praForApproval.length

  const handleDownloadOverdueInvoiceReport = async () => {
    const { data, error } = await pendingActionStore.downloadOverdueInvoiceReport()
    if (data && !error) {
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      downloadLinkRef.current.href = window.URL.createObjectURL(blob)
      downloadLinkRef.current.click()
    }
  }

  const [overdueTooltipOpen, setOverdueTooltipOpen] = useState(false)
  const [proposalTooltipOpen, setProposalTooltipOpen] = useState(false)
  const [praForApprovalTooltipOpen, setPraForApprovalTooltipOpen] = useState(false)
  const [noPoReceivedTooltipOpen, setNoPoReceivedTooltipOpen] = useState(false)

  const toggleOverdueTooltip = () => setOverdueTooltipOpen(!overdueTooltipOpen)
  const toggleProposalTooltip = () => setProposalTooltipOpen(!proposalTooltipOpen)
  const togglePraForApprovalTooltip = () => setPraForApprovalTooltipOpen(!praForApprovalTooltipOpen)
  const toggleNoPoReceivedTooltip = () => setNoPoReceivedTooltipOpen(!noPoReceivedTooltipOpen)

  React.useEffect(() => {
    pageResourceStore.get('pendingaction')
  }, [pageResourceStore])

  React.useEffect(() => {
    pendingActionStore.fetchTable()
  }, [pendingActionStore])

  return (
    <div id="pendingactions">
      <LayoutTitle title={resource.PendingAction} actionClassName="ms-4" />
      {!pageLoading && !hasData && <p className="fs-italic fs-5 m-2">{resource.NoPendingAction}</p>}
      {!!overdueInvoices && !!overdueInvoices.length && (
        <PendingActionGroup
          groupName={
            <div className="d-inline-flex align-items-center">
              {resource.OverdueInvoices}
              {/* ✅ Use a simple info icon */}
              <strong>
                <span
                  id="over-due-invoice-info"
                  className="info-icon-circle ms-2 d-inline-flex align-items-center justify-content-center"
                >
                  i
                </span>
              </strong>

              {/* ✅ Tooltip for that element */}
              <Tooltip
                placement="top"
                isOpen={overdueTooltipOpen}
                target="over-due-invoice-info"
                toggle={toggleOverdueTooltip}
              >
                {resource.OverdueInvoicesTooltip}
              </Tooltip>
            </div>
          }
          pendingActions={overdueInvoices}
          session={session}
          resource={resource}
          headerClassName="overudue-invoice-header"
          cardBodyToggleButtonClassName="overdue-invoice-toggle-button"
          cardBodyToggleIconClassName="overdue-invoice-toggle-icon"
          actions={
            <Button
              className="btn-invoice-report"
              color="white"
              text
              size="sm"
              icon="download"
              onClick={handleDownloadOverdueInvoiceReport}
            >
              {resource.DownloadReport || 'Download report'}
            </Button>
          }
        />
      )}
      {!!proposalNotSelected && !!proposalNotSelected.length && (
        <PendingActionGroup
          groupName={
            <div className="d-inline-flex align-items-center">
              {resource.ProposalNotSelected}

              {/* ✅ Use a simple info icon */}
              <strong>
                <span
                  id="proposal-not-selected-info"
                  className="info-icon-circle ms-2 d-inline-flex align-items-center justify-content-center"
                >
                  i
                </span>
              </strong>

              {/* ✅ Tooltip for that element */}
              <Tooltip
                placement="top"
                isOpen={proposalTooltipOpen}
                target="proposal-not-selected-info"
                toggle={toggleProposalTooltip}
              >
                {resource.ProposalNotSelectedTooltip}
              </Tooltip>
            </div>
          }
          pendingActions={proposalNotSelected}
          session={session}
          resource={resource}
        />
      )}
      {!!praForApproval && !!praForApproval.length && (
        <PendingActionGroup
          groupName={
            <div className="d-inline-flex align-items-center">
              {resource.ForApproval}
              <strong>
                <span
                  id="pra-for-approval-info"
                  className="info-icon-circle ms-2 d-inline-flex align-items-center justify-content-center"
                >
                  i
                </span>
              </strong>
              {/* ✅ Tooltip for that element */}
              <Tooltip
                placement="top"
                isOpen={praForApprovalTooltipOpen}
                target="pra-for-approval-info"
                toggle={togglePraForApprovalTooltip}
              >
                {resource.ForApprovalTooltip}
              </Tooltip>
            </div>
          }
          pendingActions={praForApproval}
          session={session}
          resource={resource}
        />
      )}
      {!!noPoReceived && !!noPoReceived.length && (
        <PendingActionGroup
          groupName={
            <div className="d-inline-flex align-items-center">
              {resource.ProposalSelectedNoPoReceived}
              <strong>
                <span
                  id="no-po-received-info"
                  className="info-icon-circle ms-2 d-inline-flex align-items-center justify-content-center"
                >
                  i
                </span>
              </strong>
              {/* ✅ Tooltip for that element */}
              <Tooltip
                placement="top"
                isOpen={noPoReceivedTooltipOpen}
                target="no-po-received-info"
                toggle={toggleNoPoReceivedTooltip}
              >
                {resource.ProposalSelectedNoPoReceivedTooltip}
              </Tooltip>
            </div>
          }
          pendingActions={noPoReceived}
          session={session}
          resource={resource}
        />
      )}
      <Loader show={pageLoading} text={commonResource.Loading} className="justify-content-center my-4" />
      <a href="#" ref={downloadLinkRef} download={'Overdue invoice summary'} className="d-none"></a>
    </div>
  )
})
